import React, { useEffect, useMemo, useState } from 'react';
import { catalogNodeService } from '../../../../services/apiService';

export default function FitmentAttrsForm({
  partCategory = '',
  profile,
  value = {},
  onChange,
  fieldErrors = {},
  vehiclePath = {},
  showCrossCompatPreview = true,
}) {
  const [schema, setSchema] = useState({ fields: [], groupLabel: 'Fitment details' });
  const [loading, setLoading] = useState(true);
  const [crossCompat, setCrossCompat] = useState(null);
  const [crossCompatLoading, setCrossCompatLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const res = partCategory
          ? await catalogNodeService.getFitmentFieldsByPart(partCategory)
          : await catalogNodeService.getFitmentFields(profile);
        const data = res?.data || res;
        if (!cancelled) {
          setSchema({
            fields: data?.fields || data || [],
            groupLabel: data?.groupLabel || 'Fitment details',
            inTaxonomy: data?.inTaxonomy !== false,
          });
        }
      } catch {
        if (!cancelled) setSchema({ fields: [], groupLabel: 'Fitment details' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [partCategory, profile]);

  const canPreviewCrossCompat = useMemo(
    () =>
      showCrossCompatPreview &&
      partCategory?.trim() &&
      vehiclePath?.make &&
      vehiclePath?.model &&
      vehiclePath?.generation,
    [showCrossCompatPreview, partCategory, vehiclePath]
  );

  useEffect(() => {
    if (!canPreviewCrossCompat) {
      setCrossCompat(null);
      return undefined;
    }

    const timer = setTimeout(async () => {
      setCrossCompatLoading(true);
      try {
        const res = await catalogNodeService.previewCrossCompat({
          partCategory: partCategory.trim(),
          vehiclePath,
          fitmentAttrs: value,
        });
        setCrossCompat(res?.data || res);
      } catch {
        setCrossCompat(null);
      } finally {
        setCrossCompatLoading(false);
      }
    }, 450);

    return () => clearTimeout(timer);
  }, [canPreviewCrossCompat, partCategory, vehiclePath, value]);

  const update = (key, val) => {
    onChange?.({ ...value, [key]: val });
  };

  if (loading) {
    return <p className="text-xs text-slate-500">Loading fitment fields…</p>;
  }

  if (!schema.fields.length) {
    return (
      <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-200 px-3 py-2">
        No extra fitment fields for this part type.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">
          {schema.groupLabel}
        </p>
        {!schema.inTaxonomy && (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3">
            This part type isn&apos;t in our catalog yet. You can still save a draft — we&apos;ll add
            it within 48h and notify you.
          </p>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {schema.fields.map((field) => {
            const err = fieldErrors[field.key];
            const isRequired = !!field.required;
            return (
              <div key={field.key}>
                <label className="block text-xs font-semibold text-slate-600 mb-1">
                  {field.label}
                  {isRequired ? ' *' : ''}
                </label>
                {field.type === 'select' ? (
                  <select
                    value={value[field.key] ?? ''}
                    onChange={(e) => update(field.key, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${
                      err ? 'border-red-500 bg-red-50' : 'border-slate-300'
                    }`}
                    aria-invalid={!!err}
                    aria-required={isRequired}
                  >
                    {!isRequired && <option value="">Select (optional)</option>}
                    {isRequired && (
                      <option value="" disabled>
                        Select {field.label.toLowerCase()}
                      </option>
                    )}
                    {(field.options || []).map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type={field.type === 'number' ? 'number' : 'text'}
                    value={value[field.key] ?? ''}
                    onChange={(e) => update(field.key, e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg text-sm ${
                      err ? 'border-red-500 bg-red-50' : 'border-slate-300'
                    }`}
                    aria-invalid={!!err}
                    aria-required={isRequired}
                  />
                )}
                {err ? <p className="text-xs text-red-600 mt-1">{err}</p> : null}
              </div>
            );
          })}
        </div>
      </div>

      {canPreviewCrossCompat && (
        <div className="rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
          <p className="text-[10px] font-bold uppercase tracking-wide text-indigo-600 mb-1">
            Cross-compatibility preview
          </p>
          {crossCompatLoading ? (
            <p className="text-xs text-slate-500">Checking compatible vehicles…</p>
          ) : crossCompat?.alsoFitsLabel ? (
            <p className="text-sm text-indigo-900 font-medium">{crossCompat.alsoFitsLabel}</p>
          ) : (
            <p className="text-xs text-slate-500">
              No high-confidence cross-fit vehicles yet — complete fitment fields to refine matches.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
